import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, Landmark, Link2, Link2Off, Loader2, RefreshCw } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useTranslation } from '@/hooks/useTranslation';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  useBankConnections,
  useDisconnectBankConnection,
  useLinkBankConnectionAccount,
} from '@/hooks/useBankConnections';
import { deriveFeedConnectionView } from '@/components/banking/feedConnectionView';
import { ChooseAccountDialog, describeAccount } from '@/components/banking/connect';
import { formatOrgDay } from '@/lib/dateUtils';
import { formatInstantInZone } from '@releve/shared/format/date';
import { isCreditCardType, isFeedAccountType } from '@releve/shared';

/**
 * The Bank feed section of Edit Account — where a feed is set up, repaired and
 * taken down.
 *
 * SETUP IS NOT THE DAILY ACTION. Sync belongs on the feed toolbar; Connect,
 * Reconnect, Unlink and Disconnect are configuration and belong in the account's
 * own settings, which is what this section is.
 *
 * THIS SECTION IS NOT PART OF THE FORM. Unlike `AccountCarbonSettings` — whose
 * edits ride along in `carbonDirty` and are written by the modal's Submit —
 * everything here writes immediately and independently. Nothing below is saved
 * by the account form, and nothing below is lost by cancelling it.
 *
 * EVERY CONTROL IS `type="button"`. `FullscreenFormDialog` wraps its children in
 * a `<form>`, so a bare `<button>` in here submits the ACCOUNT form — "Unlink"
 * would silently save the account.
 *
 * MOUNT IT CONDITIONALLY. `useBankConnections` lives here, and the modal has six
 * mount sites (one of which renders it unconditionally, gated only by `open`),
 * so the gate is the caller's: feature flag on, editing, a feed account type,
 * and a host that can take over the redirect.
 *
 * Dates: `valid_until` and `last_synced_at` are genuine `timestamptz` instants,
 * so the org-zone family applies and `safeFormatDate` does NOT (docs/DATES.md).
 * The days-to-expiry arithmetic is already inside `deriveFeedConnectionView` —
 * it is never recomputed here.
 */

export interface AccountBankFeedSectionProps {
  organizationId: string;
  /** The ledger (feed) account being edited — `chart_of_accounts.id`. */
  accountId: string;
  accountName: string;
  /** Resolved by the host from the account's type join, or from the types cache. */
  accountTypeName: string;
  /** The SAVED currency; null means the account inherits the organization's. */
  accountCurrency: string | null;
  /**
   * `form.formState.isDirty || carbonDirty` — the host form's complete unsaved
   * state. Carbon edits are unsaved work too, and `isDirty` alone loses them.
   */
  isHostFormDirty: boolean;
  /**
   * Hand the connect/reconnect flow back to the host, which closes this modal
   * and opens Dialog A with an explicit `returnTo`. `null` starts a brand-new
   * connection; a connection id reconnects that one.
   *
   * The host owns it because the flow ends in a full-page redirect: the modal
   * must be closed FIRST, or its `{fullscreenModal:true}` history entry survives
   * the navigation and becomes a dead Back press after the callback.
   */
  onRequestBankConnect: (connectionId: string | null) => void;
}

export function AccountBankFeedSection({
  organizationId,
  accountId,
  accountName,
  accountTypeName,
  accountCurrency,
  isHostFormDirty,
  onRequestBankConnect,
}: AccountBankFeedSectionProps) {
  const { t, i18n } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const orgTimeZone = selectedOrganization?.timezone ?? null;

  const { data: connections, isLoading } = useBankConnections(organizationId);
  const linkAccount = useLinkBankConnectionAccount(organizationId);
  const disconnectConnection = useDisconnectBankConnection(organizationId);

  const [isChooseDialogOpen, setIsChooseDialogOpen] = useState(false);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  /**
   * The connection a pending "leave this form and go to the bank" is for.
   * `undefined` means nothing is pending; `null` is a pending BRAND-NEW
   * connection — which is why this is not a boolean plus an id.
   */
  const [pendingConnectId, setPendingConnectId] = useState<string | null | undefined>(undefined);

  // `isConnectFlowTarget` is false by construction: the resume marker is a
  // property of the feed workspace's URL, and no connect flow can be mid-resume
  // while a modal is open over it.
  const view = useMemo(
    () => deriveFeedConnectionView(connections, accountId, orgTimeZone),
    [connections, accountId, orgTimeZone],
  );

  /**
   * Every bank the picker may offer: the org's live consents, plus this ledger
   * account's own connection even when it has lapsed (or Unlink would vanish
   * with the consent). Listing only one bank made the others' accounts
   * unreachable, which read as their consent having been revoked.
   */
  const chooseConnections = useMemo(() => {
    const live = (connections ?? []).filter((c) => c.status === 'authorized');
    const own = view.connection;
    return own && !live.some((c) => c.id === own.id) ? [own, ...live] : live;
  }, [connections, view.connection]);

  // Belt and braces on the host's gate — and the documentation of it. A section
  // offering to feed an Accounts Payable account would be a lie the link route
  // rejects anyway.
  if (!isFeedAccountType(accountTypeName)) return null;

  // Only RELEVE'S OWN vocabulary shifts on a credit card — the instruction, the
  // feature's name, the two labels for what is linked. Everything describing the
  // bank relationship (the consent, "your bank's screen", the disconnect warning)
  // stays, because under PSD2 a card really is authorised at its issuing bank.
  const copyContext = isCreditCardType(accountTypeName) ? 'card' : undefined;

  /**
   * The connection whose facts describe THIS account. Never falls back to the
   * org's other live connection: naming a bank that does not feed this account
   * is precisely the false status `deriveFeedConnectionView` was fixed to stop
   * producing.
   */
  const feedingConnection = view.connection;
  /**
   * The connection Dialog B lists accounts from — this account's own when
   * linked, otherwise the live one the org can link through without another
   * trip to the bank.
   */
  const chooseTarget = view.connection ?? view.linkableConnection;


  /**
   * Connect and Reconnect END IN A FULL-PAGE REDIRECT, so the SPA unloads and
   * every unsaved edit in the host form dies with it. Treat them exactly like
   * the modal's own Cancel: ask first when there is unsaved work, in the
   * dialog's OWN close-confirmation words, so the app asks one question one way.
   *
   * Unlink, Disconnect and Choose bank account never come through here — they
   * are in-place API calls that leave the form open and dirty.
   */
  const requestBankConnect = (connectionId: string | null) => {
    if (isHostFormDirty) {
      setPendingConnectId(connectionId);
      return;
    }
    onRequestBankConnect(connectionId);
  };

  const onUnlink = async () => {
    if (!view.account) return;
    try {
      await linkAccount.mutateAsync({ accountId: view.account.id, chartOfAccountsId: null });
      setConfirmingUnlink(false);
    } catch {
      // The hook owns the failure toast and the Sentry capture; the confirm
      // stays open so the click can simply be repeated.
    }
  };

  const onDisconnect = async () => {
    if (!feedingConnection) return;
    try {
      await disconnectConnection.mutateAsync({ connectionId: feedingConnection.id });
      setConfirmingDisconnect(false);
    } catch {
      // Reported by the hook; see onUnlink.
    }
  };

  if (isLoading) {
    return (
      <p className="text-sm font-normal text-muted-foreground" data-testid="account-bank-feed-loading">
        {t('loading')}
      </p>
    );
  }

  return (
    <div
      className="space-y-3"
      data-testid="account-bank-feed-section"
      /* No visible status label: the buttons beside it already say what the feed
         is (Connect / Choose / Unlink), so the badge only repeated them. The
         derived state stays readable here for tests and debugging. */
      data-state-name={view.state}
    >
      {/* No card chrome: this section already sits inside the form's own
          Card, and a bordered panel within a panel read as a nested box. */}
      <div className="space-y-3">
        {!feedingConnection && (
          <div>
            <p className="text-sm font-medium text-foreground">
              {t('bankAccounts.sync.banner.unconnectedTitle', { context: copyContext })}
            </p>
            <p className="text-xs font-normal text-muted-foreground">
              {t('bankAccounts.sync.banner.unconnectedDescription')}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* The ledger side, currency included: it is the constraint on which of
              the bank's accounts may link here, and the form's own currency field
              sits sections away. This is the SAVED currency — the feed uses what
              is on the row, not what is typed above and not yet submitted. */}
          {feedingConnection && (
            <DetailField label={t('bankAccounts.sync.connectDialog.bankLabel')}>
              {`${feedingConnection.aspsp_name} · ${feedingConnection.aspsp_country}`}
            </DetailField>
          )}

          <DetailField label={t('bankAccounts.sync.chooseAccount.ledgerAccountLabel')}>
            {accountCurrency ? `${accountName} · ${accountCurrency}` : accountName}
          </DetailField>

          {view.account && (
            <DetailField label={t('bankAccounts.sync.settings.linkedAccount', { context: copyContext })}>
              {describeAccount(view.account, t)}
            </DetailField>
          )}

          {feedingConnection?.valid_until && (
            <DetailField label={t('bankAccounts.sync.popover.consentExpires')}>
              {formatOrgDay(feedingConnection.valid_until, orgTimeZone, 'PP')}
            </DetailField>
          )}

          {view.account && (
            <DetailField label={t('bankAccounts.sync.popover.lastSynced')}>
              {view.account.last_synced_at
                ? formatInstantInZone(
                    view.account.last_synced_at,
                    orgTimeZone,
                    i18n.language,
                    'dateTime',
                  )
                : t('bankAccounts.sync.popover.neverSynced')}
            </DetailField>
          )}

          {feedingConnection?.last_error && (
            <DetailField label={t('bankAccounts.sync.popover.lastError')}>
              <span className="text-destructive">
                {/* The backend stores Enable Banking's TEXT error code. Translate
                    it; fall back to the raw code rather than showing nothing, so
                    an unmapped code is still supportable. */}
                {t(`bankAccounts.sync.errors.${feedingConnection.last_error}`, {
                  defaultValue: feedingConnection.last_error,
                })}
              </span>
            </DetailField>
          )}
        </div>

        {view.state === 'notInConsent' && (
          <p className="text-xs font-normal text-muted-foreground">
            {t('bankAccounts.sync.popover.notInConsent')}
          </p>
        )}

        {/* Actions close the section, after the facts they act on —
            the header-above-content rule (docs/FULLSCREEN_FORM_MODALS.md):
            no section head in this codebase carries actions on its line. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {view.action === 'connect' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="account-bank-feed-connect"
              onClick={() => requestBankConnect(null)}
            >
              <Landmark className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('bankAccounts.sync.connect', { context: copyContext })}
            </Button>
          )}

          {view.action === 'reconnect' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="account-bank-feed-reconnect"
              onClick={() => requestBankConnect(feedingConnection?.id ?? null)}
            >
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('bankAccounts.sync.reconnect')}
            </Button>
          )}

          {view.action === 'link' && chooseTarget && (
            <Button
              type="button"
              size="sm"
              data-testid="account-bank-feed-choose"
              onClick={() => setIsChooseDialogOpen(true)}
            >
              <Link2 className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('bankAccounts.sync.settings.chooseAccount', { context: copyContext })}
            </Button>
          )}

          {/* The org already holds a live consent, so the action above offers
              THAT bank's accounts. Without this there is no route from here to a
              SECOND institution at all: a reconnect is now pinned to its own
              bank (it must not repoint the row), so nothing else on this surface
              starts a fresh authorisation. */}
          {view.action === 'link' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="account-bank-feed-connect-other"
              onClick={() => requestBankConnect(null)}
            >
              <Landmark className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('bankAccounts.sync.settings.connectAnotherBank', { context: copyContext })}
            </Button>
          )}

          {view.account && !confirmingUnlink && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="account-bank-feed-unlink"
              onClick={() => setConfirmingUnlink(true)}
              disabled={linkAccount.isPending}
            >
              <Link2Off className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('bankAccounts.sync.chooseAccount.unlink')}
            </Button>
          )}

          {/* Already revoked — there is nothing left to withdraw. */}
          {feedingConnection && feedingConnection.status !== 'revoked' && !confirmingDisconnect && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="account-bank-feed-disconnect"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmingDisconnect(true)}
            >
              <Link2Off className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('bankAccounts.sync.settings.disconnect')}
            </Button>
          )}
        </div>
      </div>

      {confirmingUnlink && view.account && (
        <ConfirmPanel
          testId="account-bank-feed-unlink-confirm"
          title={t('bankAccounts.sync.chooseAccount.unlinkWarningTitle')}
          description={t('bankAccounts.sync.chooseAccount.unlinkWarningDescription')}
          cancelLabel={t('bankAccounts.sync.chooseAccount.unlinkCancel')}
          confirmLabel={t('bankAccounts.sync.chooseAccount.unlinkConfirm')}
          confirmTestId="account-bank-feed-unlink-confirm-action"
          isPending={linkAccount.isPending}
          onCancel={() => setConfirmingUnlink(false)}
          onConfirm={onUnlink}
        />
      )}

      {confirmingDisconnect && feedingConnection && (
        <ConfirmPanel
          testId="account-bank-feed-disconnect-confirm"
          title={t('bankAccounts.sync.settings.disconnectWarningTitle')}
          // Consent is granted PER INSTITUTION, so this ends the feed for every
          // ledger account fed by this bank, not just the one being edited.
          description={t('bankAccounts.sync.settings.disconnectWarningDescription')}
          cancelLabel={t('bankAccounts.sync.settings.disconnectCancel')}
          confirmLabel={t('bankAccounts.sync.settings.disconnectConfirm')}
          confirmTestId="account-bank-feed-disconnect-confirm-action"
          isPending={disconnectConnection.isPending}
          onCancel={() => setConfirmingDisconnect(false)}
          onConfirm={onDisconnect}
        />
      )}

      {/* Dialog B, in place: linking needs no trip to the bank, so it neither
          closes nor dirties the host form. Mounted conditionally — its body
          calls data hooks. */}
      {isChooseDialogOpen && chooseTarget && (
        <ChooseAccountDialog
          open
          onOpenChange={setIsChooseDialogOpen}
          organizationId={organizationId}
          bankAccountId={accountId}
          connections={chooseConnections}
          onReconnect={() => {
            setIsChooseDialogOpen(false);
            requestBankConnect(chooseTarget.id);
          }}
        />
      )}

      {/* The leave-warning for the two controls that unload the SPA. Reuses the
          fullscreen dialog's own close-confirmation strings — same question,
          same words, no new i18n. */}
      {pendingConnectId !== undefined && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingConnectId(undefined);
          }}
          onConfirm={() => onRequestBankConnect(pendingConnectId)}
          title={t('closeConfirmation.title')}
          description={t('closeConfirmation.description')}
          confirmText={t('closeConfirmation.discardChanges')}
          cancelText={t('closeConfirmation.keepEditing')}
          variant="destructive"
        />
      )}
    </div>
  );
}

/**
 * A read-only fact, in the shape this modal already uses for its read-only
 * Classification block: an uppercase micro label above a `text-sm font-medium`
 * value (docs/FULLSCREEN_FORM_MODALS.md "uppercase micro section heads" and
 * "thin field labels"). The class string is repeated across the form components
 * rather than shared, which is the existing convention here.
 */
const FIELD_LABEL = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className={`${FIELD_LABEL} mb-1`}>{label}</p>
      <p className="text-sm font-medium">{children}</p>
    </div>
  );
}

/**
 * The two-step confirm both destructive controls use: the button reveals this
 * panel rather than opening a dialog, mirroring the unlink confirm in
 * `ChooseAccountDialog` — and, unlike a nested dialog, it cannot fight the
 * fullscreen form for focus.
 */
function ConfirmPanel({
  testId,
  title,
  description,
  cancelLabel,
  confirmLabel,
  confirmTestId,
  isPending,
  onCancel,
  onConfirm,
}: {
  testId: string;
  title: string;
  description: string;
  cancelLabel: string;
  confirmLabel: string;
  confirmTestId: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Alert variant="warning" className="space-y-2" data-testid={testId}>
      {/* The glyph sits INSIDE the title rather than as a direct child of Alert:
          the primitive absolutely-positions a direct `> svg`, which is right for
          a banner but not for this compact confirm, where the icon reads as part
          of the sentence. It inherits the variant's colour via currentColor. */}
      <AlertTitle className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {title}
      </AlertTitle>
      <AlertDescription className="text-xs font-normal text-muted-foreground">
        {description}
      </AlertDescription>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          data-testid={confirmTestId}
          onClick={onConfirm}
          disabled={isPending}
        >
          {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
          {confirmLabel}
        </Button>
      </div>
    </Alert>
  );
}
